/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import {
  ActivityIndicator,
  Appearance, StyleProp, TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
} from "react-native"
import { Cell, FloatButton } from "../cycle-water-quality-multi-measurement-list/cycle-water-quality-multi-measurement-list-screen"
import {
  ActionsheetIosAware,
  Button,
  DeleteModal,
  FlatList,
  JalaIcon,
  JalaLoading,
  LazyRender,
  ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
} from "../../../../components"
import { Icon, Skeleton, View, Actionsheet, useDisclose } from "native-base"
import { SafeAreaView } from "react-native-safe-area-context"
import { color, nativeBaseTheme } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate, TxKeyPath } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import SyncableModel from "../../../../database/SyncableModel"
import { MultiMeasurement as Model } from "../../../../database"
import { chemicalParameter } from "../../parameter-list/parameter-list-screen"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const FLEX: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const HEADER: ViewStyle = {
  ...FLEX,
  paddingHorizontal: 20,
  alignItems: "center",
}

const CONTENT_ACTIONSHEET: ViewStyle = {
  flex: 1,
  paddingHorizontal: 20,
  justifyContent: 'space-evenly'
}

export interface OptionActionsheetProps {
  isOpen: boolean
  colorScheme?: "light" | "dark"

  onNavigate?: () => void
  onClose: () => void
  onEdit?: () => void
  onDelete?: () => void
  data?: any

  /**
   * A style override to apply to the container.  Useful for margins and paddings.
   */
  style?: StyleProp<ViewStyle>
}

const TableHead: FC<{ colorScheme?: "light" | "dark", parameter?: Array<any> }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), parameter } = props;

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <View
    borderWidth="1px"
    borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
    backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
    flexDirection="row"
  >
    <LazyRender renderPlaceholder={renderPlaceholder}>
      <Cell text={`${_.upperFirst(translate('multiMeasurements.measured_date'))} (DOC)`} width="104px" textPreset="body3" colorScheme={colorScheme} />
      {/* <Cell text={`${_.upperFirst(translate('multiMeasurements.ammonia_short'))}`} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={`${_.upperFirst(translate('multiMeasurements.nitrit_short'))}`} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={`${_.upperFirst(translate('multiMeasurements.alkalinity_short'))}`} textPreset="body3" colorScheme={colorScheme} /> */}
      {parameter.map(param => <Cell key={param.id} text={`${_.upperFirst(translate(`multiMeasurements.${param.code}_short` as TxKeyPath))}`} textPreset="body3" colorScheme={colorScheme} />)}
      <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" alignItems="center" justifyContent="center" textPreset="body3" colorScheme={colorScheme} />
    </LazyRender>
  </View>;
});

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void, parameter?: Array<any> }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info,
    handleEdit, parameter
  } = props;
  const {
    item,
    item: {
      measured_at, age, _pushing, modified
    }
  } = info;

  const measuredAt = moment(measured_at);

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });

  const renderPlaceholder = useCallback(() => {
    return <View>
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  const valueFormatted = (value) => (
    value != null && numeral(value).format('0,0.[00]') || '-'
  )

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View flexDirection="row" position="relative">
      <Cell text={`${measuredAt.format('DD MMM')} (${age})`} width="104px" colorScheme={colorScheme} />
      {/* <Cell text={valueFormatted(ammonia)} colorScheme={colorScheme} textColor="primary.700" />
      <Cell text={valueFormatted(nitrit)} colorScheme={colorScheme} textColor="primary.700" />
      <Cell text={valueFormatted(alkalinity)} colorScheme={colorScheme} textColor="primary.700" /> */}
      {parameter.map(param => <Cell key={param.id} text={valueFormatted(item[param.code])} colorScheme={colorScheme} textColor="primary.700" />)}
      <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
        {_pushing
          && <ActivityIndicator size={15} />
          || <TouchableOpacity onPress={onPressEdit}><Icon as={JalaIcon} name="more_vert" size="20px" color="primary.500"/></TouchableOpacity>}
      </Cell>
      {modified && (
        <Cell
          width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
          justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
        >
          <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
        </Cell>
       )}
    </View>
  </LazyRender>;
})

export const OptionActionsheet = (props: OptionActionsheetProps) => {
  const { isOpen, onClose, onEdit, colorScheme = "light" || Appearance.getColorScheme(), onNavigate, onDelete } = props;

  return (
    <ActionsheetIosAware isOpen={isOpen} onClose={onClose}>
      <Actionsheet.Content alignItems="stretch" px={0} py={0} height="200px">
        <View style={HEADER}>
          <Text preset="title2" color={color.primaryDarker} tx="chemical.more" />
          <Button testID="jala:id/cancel-button" preset="link" textPreset="title2" tx="cancel" onPress={onClose} colorScheme={colorScheme} textColor={color.primary} />
        </View>
        <View style={CONTENT_ACTIONSHEET}>
          <Button testID="jala:id/edit-button" preset="link" textPreset="body1" tx="edit" onPress={onEdit} textColor={color.palette.darkGrey} colorScheme={colorScheme} />
          <Button testID="jala:id/change-button" preset="link" textPreset="body1" tx="chemical.changeParam" onPress={onNavigate} textColor={color.palette.darkGrey} colorScheme={colorScheme} />
          <DeleteModal
            testID="jala:id/delete-button"
            onConfirm={onDelete}
            // deleteModalTitle={deleteModalTitle}
            // deleteModalSubtitle={deleteModalSubtitle}
            // disabled={loading}
          >
            <Text
              tx="delete"
              preset="body1"
              color={color.palette.darkGrey}
              colorScheme={colorScheme}
              // onPress={onClose}
              // color={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
            />
          </DeleteModal>
        </View>
      </Actionsheet.Content>
    </ActionsheetIosAware>

  )
}

export const CycleChemicalMultiMeasurementListScreen: FC<{ colorScheme?: "light" | "dark", onSuccess?: (success: boolean) => boolean  }> = observer(function ChemicalMultiMeasurementTableScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), onSuccess } = props
  const { height } = useWindowDimensions();

  const [ dataItem, setDataItem ] = useState<any>({})
  const [ model, setModel ] = useState<SyncableModel>(null)

  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore: {
        parameter,
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            chemicalMultiMeasurements: models,
            loadingChemicalMultiMeasurement: loadingModel
          }
        }
      }
    },
    chemicalMultiMeasurementStore: modelStore,
    chemicalMultiMeasurementStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
  } = useStores();

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  const modelFlats = useMemo(() => {
    return models.filter(({ measured_date }) => (
      (!dateTimeFilter.date_after || dateTimeFilter.date_after <= measured_date)
      && (!dateTimeFilter.date_before || measured_date <= dateTimeFilter.date_before)
    )).map((m) => ({
      ...m,
      _pushing: !!pushIdQueue[m.id],
      _loading: loadingModel
    }))
  }, [dateTimeFilter, loadingModel, models, pushIdQueue])

  const { isOpen, onOpen, onClose } = useDisclose(false)

  const parametersCode = parameter.map((p) => p.code).join(',')
  const initialParameter = useMemo(() => {
    return parametersCode ? parameter : chemicalParameter.slice(0, 3);
  },[parameter, parametersCode])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - chemical');
  }, []))

  useEffect(() => {
    modelStore.init();
    cycleStore.getChemicalMultiMeasurements(!isOffline).catch();
  }, [cycleStore, isOffline, modelStore])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        cycleStore.getChemicalMultiMeasurements(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getChemicalMultiMeasurements(false).then(() => {
          cycleStore.getChemicalMultiMeasurements(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, isOffline, modelStore, needRefresh])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add chemical');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.ChemicalMultiMeasurement.Create', { pond_id: cycle.pond_id, cycle_id: cycle.id });
  }, [cycle])

  // const handleEdit = useCallback((item) => {
  //   navigate('Home.Stack', 'Home.ChemicalMultiMeasurement.Edit', { ...item })
  // }, [])

  const handleOpen = useCallback((item) => {
    setDataItem(item);
    const chemicalMultiMeasurement = models.find((m) => m.id === item.id);
    if (chemicalMultiMeasurement) {
      const model = Model.hydrate({ ...chemicalMultiMeasurement })
      setModel(model)
    }
    onOpen()
  }, [models, onOpen])

  const handleEdit = () => {
    onClose();
    navigate('Home.Stack', 'Home.ChemicalMultiMeasurement.Edit', { ...dataItem });
  }

  const handleNavigate = () => {
    onClose();
    navigate('Home.Stack', 'Home.Parameter.List', {route: 'chemical'});
  }

  const handleDelete = useCallback(async () => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = await modelStore.delete(model)
    if ((!onSuccess || onSuccess(success)) && success) {
      onClose()
    }
    modelStore.set('loading', false)
  }, [model, modelStore, onClose, onSuccess])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    // return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleOpen} parameter={initialParameter} />
  // }, [colorScheme, handleEdit])
  }, [colorScheme, handleOpen, initialParameter])

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      cycleStore.getChemicalMultiMeasurements(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelFlats.length && <TableHead colorScheme={colorScheme} parameter={initialParameter} />}
      <FlatList
        key={initialParameter.map((param) => param.code).join(",")}
        data={modelFlats}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelFlats.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={40}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
      />}
      <OptionActionsheet
        isOpen={isOpen}
        colorScheme={colorScheme}
        onClose={onClose}
        onEdit={handleEdit}
        onNavigate={handleNavigate}
        onDelete={handleDelete}
      />
      {/* {console.log("MODEL FLATS ====> ", modelFlats)} */}
    </Screen>
  )
})
